import React from 'react';

import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import * as PATHS from 'new/constants/pathnames';
import { ScreenComponent, ScreenConfig } from 'new/routes/RouterManager';

import { CapsulesFetchErrorAlert } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/CapsulesFetchErrorAlert';
import { CapsulesInitialLoading } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/CapsulesInitialLoading';
import { RegisterCapsuleModal } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal';
import { CapsulesSettingsHeader } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/CapsulesSettingsHeader';
import { CapsulesSettingsLayout } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/CapsulesSettingsLayout';
import { CapsulesTableRegion } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/CapsulesTable/CapsulesTableRegion';
import { useCapsulesSettingsScreen } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/hooks';

interface CapsulesSettingsScreenProps {}

export const CapsulesSettingsScreen: ScreenComponent<CapsulesSettingsScreenProps> = () => {
  const {
    rows,
    refetch,
    error,
    isCreating,
    canRegisterCapsule,
    showError,
    showInitialLoading,
    showFetchingHint,
    isRegisterCapsuleModalOpen,
    handleRegisterCapsule,
    handleCloseRegisterCapsuleModal,
    handleContinueToDeploy,
    handleRegisterKeys,
    handleRowClick,
    handleRowMenuAction,
    ConfirmDialogComponent,
    page,
    pageCount,
    onPageChange,
    showPagination,
  } = useCapsulesSettingsScreen();

  const banner = showError ? <CapsulesFetchErrorAlert error={error} onRetry={refetch} /> : null;

  let mainContent: React.ReactNode;
  if (showInitialLoading) {
    mainContent = <CapsulesInitialLoading />;
  } else {
    mainContent = (
      <CapsulesTableRegion
        rows={rows}
        onRowClick={canRegisterCapsule ? handleRowClick : undefined}
        onRowMenuAction={handleRowMenuAction}
        showRowActions={canRegisterCapsule}
        showFetchingHint={showFetchingHint}
        showPagination={showPagination}
        page={page}
        pageCount={pageCount}
        onPageChange={onPageChange}
      />
    );
  }

  return (
    <>
      {ConfirmDialogComponent}

      {canRegisterCapsule ? (
        <RegisterCapsuleModal
          open={isRegisterCapsuleModalOpen}
          onClose={handleCloseRegisterCapsuleModal}
          onContinueToDeploy={handleContinueToDeploy}
          onClickRegisterKeys={handleRegisterKeys}
          isCreatingCluster={isCreating}
        />
      ) : null}

      <CapsulesSettingsLayout
        header={
          <CapsulesSettingsHeader
            showRegisterAction={canRegisterCapsule}
            registerDisabled={isRegisterCapsuleModalOpen}
            onRegisterCapsule={handleRegisterCapsule}
          />
        }
        banner={banner}
        main={mainContent}
      />
    </>
  );
};

CapsulesSettingsScreen.screenConfig = {
  path: `${ROUTE_REDESIGN}${PATHS.ROUTE_SETTINGS_CAPSULES}`,
  exact: true,
  screenName: 'CapsulesSettings',
  domain: 'settings',
  pageTitle: 'Capsules',
  header: {
    title: 'Capsules',
    slugTitle: 'Capsules',
  },
} as ScreenConfig<CapsulesSettingsScreenProps>;
